/**
 * LinkEngine - Creates and manages links between various stores and items
 * 
 * This engine monitors store changes and creates relationships between different data types.
 * It handles folder organization, item categorization, and cross-references between stores.
 */

import { storeRegistry } from './GenericStore';
import { objectsStore, GlossaryItem } from "@/lib/stores/objectsStore";
import { documentStore } from '@/lib/stores/documentStore';
import { templatesStore } from "@/lib/stores/templatesStore";
import { dialogStore } from '@/lib/stores/dialogStore';
import { logicFlowStore } from '@/lib/stores/logicFlowStore';
import { typesStore } from '@/lib/stores/typesStore';
import { gameFoldersStore, Folder, GameFolders } from '@/lib/stores/folderStore';

// Types for link management
export interface FolderItemLink {
  folderId: string | null;
  itemId: string;
  itemType: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable';
  gameId: string;
}

export interface FolderItemCount {
  folderId: string;
  count: number;
  itemType: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable';
}

export interface LinkEngineState {
  isInitialized: boolean;
  storesLoaded: {
    glossary: boolean;
    documents: boolean;
    blueprints: boolean;
    dialogs: boolean;
    flows: boolean;
    variables: boolean;
    folders: boolean;
  };
  folderItemLinks: FolderItemLink[];
  folderItemCounts: Map<string, FolderItemCount>;
}

class LinkEngine {
  private state: LinkEngineState = {
    isInitialized: false,
    storesLoaded: {
      glossary: false,
      documents: false,
      blueprints: false,
      dialogs: false,
      flows: false,
      variables: false,
      folders: false,
    },
    folderItemLinks: [],
    folderItemCounts: new Map(),
  };

  private listeners: Array<(state: LinkEngineState) => void> = [];
  private unsubscribeFunctions: Array<() => void> = [];

  constructor() {
    this.initialize();
  }

  /**
   * Initialize the link engine and set up store subscriptions
   */
  private initialize() {

    // Subscribe to all relevant stores
    this.setupStoreSubscriptions();
    
    this.state.isInitialized = true;
    this.notifyListeners();
  }

  /**
   * Set up subscriptions to all relevant stores
   */
  private setupStoreSubscriptions() {
    // Subscribe to glossary store
    const unsubGlossary = objectsStore.subscribe((items) => {
      this.state.storesLoaded.glossary = true;
      this.updateFolderLinks();
    });

    // Subscribe to document store
    const unsubDocuments = documentStore.subscribe((items) => {
      this.state.storesLoaded.documents = true;
      this.updateFolderLinks();
    });

    // Subscribe to blueprints store
    const unsubBlueprints = templatesStore.subscribe((items) => {
      this.state.storesLoaded.blueprints = true;
      this.updateFolderLinks();
    });

    // Subscribe to dialogs store
    const unsubDialogs = dialogStore.subscribe((items) => {
      this.state.storesLoaded.dialogs = true;
      this.updateFolderLinks();
    });

    // Subscribe to flows store
    const unsubFlows = logicFlowStore.subscribe((items) => {
      this.state.storesLoaded.flows = true;
      this.updateFolderLinks();
    });

    // Subscribe to variables (types) store
    const unsubVariables = typesStore.subscribe((items) => {
      this.state.storesLoaded.variables = true;
      this.updateFolderLinks();
    });

    // Subscribe to folders store
    const unsubFolders = gameFoldersStore.subscribe((gameFolders) => {
      this.state.storesLoaded.folders = true;
      this.updateFolderLinks();
    });

    // Store unsubscribe functions for cleanup
    this.unsubscribeFunctions = [
      unsubGlossary,
      unsubDocuments,
      unsubBlueprints,
      unsubDialogs,
      unsubFlows,
      unsubVariables,
      unsubFolders,
    ];
  }

  /**
   * Update folder-item links based on current store data
   */
  private updateFolderLinks() {
    
    const newLinks: FolderItemLink[] = [];
    const folderCounts = new Map<string, FolderItemCount>();

    // Process glossary items
    const glossaryItems = objectsStore.getData();
    glossaryItems.forEach((item: GlossaryItem) => {
      const link: FolderItemLink = {
        folderId: item.folder || null,
        itemId: item.id,
        itemType: 'glossary',
        gameId: item.game_id,
      };
      newLinks.push(link);

      // Update folder counts
      const folderId = item.folder || 'root';
      const countKey = `${item.game_id}-${folderId}-glossary`;
      const existing = folderCounts.get(countKey);
      if (existing) {
        existing.count++;
      } else {
        folderCounts.set(countKey, {
          folderId: item.folder || 'root',
          count: 1,
          itemType: 'glossary',
        });
      }
    });

    // Process document items
    const documentItems = documentStore.getData();
    documentItems.forEach((item: any) => {
      const link: FolderItemLink = {
        folderId: item.folder || null,
        itemId: item.id,
        itemType: 'document',
        gameId: item.game_id,
      };
      newLinks.push(link);

      // Update folder counts
      const folderId = item.folder || 'root';
      const countKey = `${item.game_id}-${folderId}-document`;
      const existing = folderCounts.get(countKey);
      if (existing) {
        existing.count++;
      } else {
        folderCounts.set(countKey, {
          folderId: item.folder || 'root',
          count: 1,
          itemType: 'document',
        });
      }
    });

    // Process blueprint items
    const blueprintItems = templatesStore.getData();
    blueprintItems.forEach((item: any) => {
      const link: FolderItemLink = {
        folderId: item.folder || null,
        itemId: item.id,
        itemType: 'blueprint',
        gameId: item.game_id,
      };
      newLinks.push(link);

      // Update folder counts
      const folderId = item.folder || 'root';
      const countKey = `${item.game_id}-${folderId}-blueprint`;
      const existing = folderCounts.get(countKey);
      if (existing) {
        existing.count++;
      } else {
        folderCounts.set(countKey, {
          folderId: item.folder || 'root',
          count: 1,
          itemType: 'blueprint',
        });
      }
    });

    // Process dialog items
    const dialogItems = dialogStore.getData();
    dialogItems.forEach((item: any) => {
      const link: FolderItemLink = {
        folderId: item.folder || null,
        itemId: item.id,
        itemType: 'dialog',
        gameId: item.game_id,
      };
      newLinks.push(link);

      // Update folder counts
      const folderId = item.folder || 'root';
      const countKey = `${item.game_id}-${folderId}-dialog`;
      const existing = folderCounts.get(countKey);
      if (existing) {
        existing.count++;
      } else {
        folderCounts.set(countKey, {
          folderId: item.folder || 'root',
          count: 1,
          itemType: 'dialog',
        });
      }
    });

    // Process flow items
    const flowItems = logicFlowStore.getData();
    flowItems.forEach((item: any) => {
      const link: FolderItemLink = {
        folderId: item.folder || null,
        itemId: item.id,
        itemType: 'flow',
        gameId: item.game_id,
      };
      newLinks.push(link);

      // Update folder counts
      const folderId = item.folder || 'root';
      const countKey = `${item.game_id}-${folderId}-flow`;
      const existing = folderCounts.get(countKey);
      if (existing) {
        existing.count++;
      } else {
        folderCounts.set(countKey, {
          folderId: item.folder || 'root',
          count: 1,
          itemType: 'flow',
        });
      }
    });

    // Update state
    this.state.folderItemLinks = newLinks;
    this.state.folderItemCounts = folderCounts;




    this.notifyListeners();
  }

  /**
   * Get items for a specific folder and item type
   */
  public getItemsInFolder(gameId: string, folderId: string | null, itemType: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable'): any[] {
    const links = this.state.folderItemLinks.filter(
      link => link.gameId === gameId && 
               link.folderId === folderId && 
               link.itemType === itemType
    );

    const itemIds = links.map(link => link.itemId);

    // Get actual items from the appropriate store
    switch (itemType) {
      case 'glossary':
        return objectsStore.getData().filter(item => itemIds.includes(item.id));
      case 'document':
        return documentStore.getData().filter(item => itemIds.includes(item.id));
      case 'blueprint':
        return templatesStore.getData().filter(item => itemIds.includes(item.id));
      case 'dialog':
        return dialogStore.getData().filter(item => itemIds.includes(item.id));
      case 'flow':
        return logicFlowStore.getData().filter(item => itemIds.includes(item.id));
      case 'variable':
        return typesStore.getData().filter(item => itemIds.includes(item.id));
      default:
        return [];
    }
  }

  /**
   * Get all items organized by folders for a specific game and item type
   */
  public getItemsByFolders(gameId: string, itemType: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable'): Map<string, any[]> {
    const itemsByFolder = new Map<string, any[]>();
    
    // Get all items for this game and item type
    let allItems: any[] = [];
    switch (itemType) {
      case 'glossary':
        allItems = objectsStore.getData().filter(item => item.game_id === gameId);
        break;
      case 'document':
        allItems = documentStore.getData().filter(item => item.game_id === gameId);
        break;
      case 'blueprint':
        allItems = templatesStore.getData().filter(item => item.game_id === gameId);
        break;
      case 'dialog':
        allItems = dialogStore.getData().filter(item => item.game_id === gameId);
        break;
      case 'flow':
        allItems = logicFlowStore.getData().filter(item => item.game_id === gameId);
        break;
      case 'variable':
        allItems = typesStore.getData().filter(item => item.game_id === gameId);
        break;
    }

    // Group items by folder
    allItems.forEach(item => {
      const folderId = item.folder || 'root';
      if (!itemsByFolder.has(folderId)) {
        itemsByFolder.set(folderId, []);
      }
      itemsByFolder.get(folderId)!.push(item);
    });

    return itemsByFolder;
  }

  /**
   * Get item count for a specific folder
   */
  public getFolderItemCount(gameId: string, folderId: string | null, itemType: 'glossary' | 'document' | 'blueprint' | 'dialog'): number {
    const countKey = `${gameId}-${folderId || 'root'}-${itemType}`;
    const count = this.state.folderItemCounts.get(countKey);
    return count ? count.count : 0;
  }

  /**
   * Get all folder item counts for a game and item type
   */
  public getAllFolderCounts(gameId: string, itemType: 'glossary' | 'document' | 'blueprint' | 'dialog'): Map<string, number> {
    const counts = new Map<string, number>();
    
    for (const [key, value] of this.state.folderItemCounts.entries()) {
      if (key.startsWith(`${gameId}-`) && key.endsWith(`-${itemType}`)) {
        // Extract folder ID from key: gameId-folderId-itemType
        const parts = key.split('-');
        const folderId = parts.slice(1, -1).join('-'); // Handle folder IDs that might contain dashes
        counts.set(folderId === 'root' ? 'root' : folderId, value.count);
      }
    }
    
    return counts;
  }

  /**
   * Update folder assignment for an item
   */
  public updateItemFolder(itemId: string, itemType: 'glossary' | 'document' | 'blueprint' | 'dialog', newFolderId: string | null): void {
    // Find and update the link
    const linkIndex = this.state.folderItemLinks.findIndex(
      link => link.itemId === itemId && link.itemType === itemType
    );

    if (linkIndex !== -1) {
      this.state.folderItemLinks[linkIndex].folderId = newFolderId;
      this.updateFolderLinks(); // Recalculate counts
    }
  }

  /**
   * Check if all required stores are loaded
   */
  public areStoresLoaded(): boolean {
    return Object.values(this.state.storesLoaded).every(loaded => loaded);
  }

  /**
   * Get current engine state
   */
  public getState(): LinkEngineState {
    return { ...this.state };
  }

  /**
   * Subscribe to engine state changes
   */
  public subscribe(listener: (state: LinkEngineState) => void): () => void {
    this.listeners.push(listener);
    
    // Call listener immediately with current state
    listener(this.state);
    
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.state));
  }

  /**
   * Clean up subscriptions
   */
  public destroy() {
    this.unsubscribeFunctions.forEach(unsub => unsub());
    this.listeners = [];
  }
}

// Create singleton instance
export const linkEngine = new LinkEngine();

// Export utility functions for easy access
export const getLinkEngineState = () => linkEngine.getState();
export const getItemsInFolder = (gameId: string, folderId: string | null, itemType: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable') => 
  linkEngine.getItemsInFolder(gameId, folderId, itemType);
export const getItemsByFolders = (gameId: string, itemType: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable') => 
  linkEngine.getItemsByFolders(gameId, itemType);
export const getFolderItemCount = (gameId: string, folderId: string | null, itemType: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable') => 
  linkEngine.getFolderItemCount(gameId, folderId, itemType);
export const getAllFolderCounts = (gameId: string, itemType: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable') => 
  linkEngine.getAllFolderCounts(gameId, itemType);
export const subscribeLinkEngine = (listener: (state: LinkEngineState) => void) => 
  linkEngine.subscribe(listener);
